/**
 * The editorial rehype sequence, defined ONCE.
 *
 * Both the renderer and the out-of-Vite verification harness build their
 * pipeline from this array, so the harness cannot validate a tree production
 * does not render.
 *
 * ORDER IS THE CONTRACT, and it is load-bearing at every step:
 *   1. structure  — scrubs reserved namespaces before anything reads them, and
 *                   records heading adjacency while it still exists.
 *   2. title lift — consumes the headings that adjacency was recorded from.
 *   3. typesetting— inline marks; runs after the lift so no source-model
 *                   consumer ever reads decorated hast.
 *   4. blocks     — block roles, which read the marks step 3 produced.
 *   5. tail anchor— last, because it must select the tail of the FINAL tree.
 *
 * Raw-HTML promotion is deliberately NOT here. The renderer needs `rehypeRaw`
 * ahead of this sequence; the harness's processor already applies it while
 * parsing. Putting it in this array would double-apply it on one side.
 */
import type { Plugin } from "unified";

import { POLLY_EDITORIAL_V1 } from "./editorial-policy";
import { rehypeApplyEditorialBlocks } from "./rehype-editorial-blocks";
import { rehypeApplyEditorialTypesetting } from "./rehype-editorial-typesetting";
import {
    rehypeMarkEditorialStructure,
    rehypeScrubReservedNamespace,
} from "./rehype-editorial-structure";
import { rehypeInferTableTitle } from "./rehype-infer-table-title";
import { rehypeMarkStreamTail } from "./rehype-stream-tail-anchor";

/**
 * The editorial chain, in order.
 *
 * SHARE THE RESULT. `markdown-text.tsx` calls this once and hoists the array to
 * module scope, so neither the array nor the two factory calls are rebuilt on
 * every render — and, during streaming, every token is a render.
 *
 * What that saving is NOT: a saved pipeline rebuild. Measured, because the
 * obvious rationale is wrong — neither `@assistant-ui/react-markdown` nor
 * `react-markdown` contains a single `useMemo` or `memo()`, and react-markdown
 * calls `unified()` to construct its processor on every render regardless of
 * whether the plugin array is the same object. Sharing saves allocation, not
 * pipeline construction. Anyone optimising here should start by measuring that
 * claim rather than inheriting it.
 *
 * That is safe because every plugin in this chain is stateless across runs. The
 * one factory taking options is curried and captures only them —
 * `rehypeApplyEditorialTypesetting(options) => () => (tree) => void` — and every
 * transformer derives everything else from the tree it is handed. The layer's
 * one piece of module-level mutable state, the global-flag matcher in
 * `emoji-typesetting.ts`, is module-scoped however this array is built and
 * resets its `lastIndex` in a `finally`.
 *
 * This is a CONTRACT, not an observation, and it is pinned rather than trusted:
 * `__tests__/editorial-pipeline-sharing.test.ts` renders a sequence of documents
 * through one shared array and through a fresh array per document, and requires
 * byte-identical output. Add a plugin that accumulates state across invocations
 * and that test fails — which is precisely when production would begin bleeding
 * one message's state into the next.
 */
export const editorialRehypePlugins = (): Plugin[] =>
    [
        rehypeMarkEditorialStructure,
        rehypeInferTableTitle,
        rehypeApplyEditorialTypesetting({ policy: POLLY_EDITORIAL_V1 }),
        rehypeApplyEditorialBlocks,
        rehypeMarkStreamTail,
    ] as unknown as Plugin[];

/**
 * The PLAIN sequence, defined once for the same reason.
 *
 * A second copy — one array in the renderer, another in the harness — would
 * drift silently, and that matters more than it looks: `anchor-policy-parity`
 * judges the editorial tree AGAINST the plain one, so a plugin added to the
 * renderer's plain path and not the harness's would not fail anything. It
 * would silently move the baseline the parity check compares to, and the
 * check would keep passing while measuring the wrong thing.
 *
 * The tail anchor is here and not policy-gated: it is the streaming cursor and
 * has to run under both policies, or every plain render loses it — including
 * the fail-open fallback of a message that is still streaming.
 *
 * The namespace scrub is here for the same reason turned around. The treatment
 * CSS is not policy-scoped and both mounts set `aui-md`, so an authored
 * `data-editorial-*` attribute paints a treatment on whichever path renders it.
 * Leaving the scrub on the editorial path alone would have made the fail-open
 * fallback the spoofable one — the path that runs precisely when a message has
 * already misbehaved.
 */
export const plainRehypePlugins = (): Plugin[] =>
    [
        rehypeScrubReservedNamespace,
        rehypeInferTableTitle,
        rehypeMarkStreamTail,
    ] as unknown as Plugin[];

/**
 * The order, as names, for a drift guard to assert against. Kept beside the
 * arrays so a plugin added above without a name added here fails the guard —
 * which is the point: the guard has to be harder to satisfy than the mistake.
 */
export const PLAIN_PIPELINE_ORDER = [
    "rehypeScrubReservedNamespace",
    "rehypeInferTableTitle",
    "rehypeMarkStreamTail",
] as const;

export const EDITORIAL_PIPELINE_ORDER = [
    "rehypeMarkEditorialStructure",
    "rehypeInferTableTitle",
    "rehypeApplyEditorialTypesetting",
    "rehypeApplyEditorialBlocks",
    "rehypeMarkStreamTail",
] as const;
